/* ════ BUILD BODY + RENDER BLOCK ═══════════════════════════ */
import { state } from '../state.js';
import { LEAVE_PENDING_NAME } from '../config.js';
import { $ } from '../dom.js';
import { ddiff, addD, isWE, isMon } from '../utils/date.js';
import { startHalf, endHalf } from './util.js';
import { computeLanes } from './lanes.js';
import { setupDraw } from './draw.js';
import { startMove } from './move.js';
import { startResize } from './resize.js';
import { openEdit } from '../modals/task.js';
import { matchFilter } from '../filtermatch.js';
import { clickSuppressed } from './dragguard.js';
import { canSeeTask } from '../privacy.js';
import { gbp } from '../utils/money.js';
import { catClass, catGradient, isBuiltin } from '../categories.js';
import { specialDay, breakEves } from '../seasonal.js';
import { fdate } from '../utils/date.js';

export function buildBody() {
  const sbR = $('sb-rows'), body = $('gantt-body');
  sbR.innerHTML = ''; body.innerHTML = '';
  const { viewDays, DAY_W, LANE_H } = state;
  $('tl-inner').style.width = `${viewDays * DAY_W}px`;
  const todayMid = new Date(); todayMid.setHours(0, 0, 0, 0);
  const ti = ddiff(state.viewStart, todayMid);
  const visible = state.tasks.filter(matchFilter);
  const me = String(state.currentUser?.email || '').toLowerCase(), eves = breakEves();
  const days = Array.from({ length: viewDays }, (_, i) => addD(state.viewStart, i));
  const special = days.map(specialDay), dayIso = days.map(fdate);
  // When filtering by a resource, only show that resource's lane(s);
  // a role filter narrows to lanes whose resource holds that role.
  let resList = state.filters.resource === 'all'
    ? state.resources
    : state.resources.filter(r => r.id === state.filters.resource);
  if (state.filters.role && state.filters.role !== 'all')
    resList = resList.filter(r => (r.role || '') === state.filters.role);
  resList.forEach((res, ri) => {
    const alt = ri % 2 === 1 ? ' alt' : '';
    const rt = visible.filter(t => t.resource === res.id);
    const lanes = computeLanes(rt), numL = Math.max(1, lanes.length), secH = numL * LANE_H;
    const nc = document.createElement('div'); nc.className = 'res-cell' + alt; nc.style.height = `${secH}px`;
    nc.innerHTML = `<div class="res-av" style="background:${res.col}22;color:${res.col}">${res.ini}</div>`
      + `<div class="res-meta"><span class="res-nm">${res.name}</span>${res.role ? `<span class="res-role-lbl">${res.role}</span>` : ''}</div>`;
    sbR.appendChild(nc);
    const sec = document.createElement('div'); sec.className = 'res-sec' + alt; sec.dataset.resource = res.id;
    for (let li = 0; li < numL; li++) {
      const row = document.createElement('div'); row.className = 'lane-row'; row.style.height = `${LANE_H}px`; row.dataset.resource = res.id;
      const mine = me && String(res.user_email || '').toLowerCase() === me;
      for (let i = 0; i < viewDays; i++) {
        const d = days[i];
        const bg = document.createElement('div');
        bg.className = 'dy-bg' + (isWE(d) ? ' we' : '') + (isMon(d) ? ' mon' : '') + (i === ti ? ' tod' : '')
          + (special[i] ? ` sd-${special[i].k}` : '');
        if (mine && li === 0 && eves.has(dayIso[i])) { bg.classList.add('eyb'); bg.title = 'Last day before your break. Enjoy it! 🌴'; }
        bg.style.width = `${DAY_W}px`; bg.dataset.day = i; bg.dataset.resource = res.id;
        row.appendChild(bg);
      }
      sec.appendChild(row);
    }
    if (ti >= 0 && ti < viewDays) { const tl = document.createElement('div'); tl.className = 'today-line'; tl.style.left = `${ti * DAY_W + DAY_W / 2}px`; sec.appendChild(tl); }
    lanes.forEach((lane, li) => lane.forEach(t => renderBlock(t, li, sec)));
    const ghost = document.createElement('div'); ghost.className = 'draw-ghost'; ghost.id = `g-${res.id}`; sec.appendChild(ghost);
    setupDraw(sec, res.id);
    body.appendChild(sec);
  });
}

export function renderBlock(t, li, sec) {
  const { viewDays, DAY_W, LANE_H } = state;
  const vh = viewDays * 2, HW = DAY_W / 2;
  const sh = startHalf(t), eh = endHalf(t);
  if (eh <= 0 || sh >= vh) return;
  const cs = Math.max(0, sh), ce = Math.min(vh, eh);
  const w = Math.max((ce - cs) * HW - 3, 14);
  const imp = t.importance || 'normal';
  const el = document.createElement('div');
  el.id = `tb-${t.id}`;
  el.style.cssText = `left:${cs * HW + 1}px;top:${li * LANE_H + 7}px;width:${w}px;height:${LANE_H - 14}px`;

  // Private task you can't see: a neutral "Private" block (no detail, no edit).
  if (!canSeeTask(t)) {
    el.className = 'task-block private';
    el.title = 'Private';
    const p = document.createElement('span'); p.className = 'tb-private'; p.textContent = '🔒 Private';
    el.appendChild(p); sec.appendChild(el); return;
  }

  el.className = `task-block ${catClass(t.cat)} imp-${imp}` + (t.locked ? ' locked' : '') + (t.completed ? ' done' : '')
    + (t.cat === 'ooo' && t.name === LEAVE_PENDING_NAME ? ' leave-pending' : '');
  if (!isBuiltin(t.cat)) el.style.background = catGradient(t.cat);   // custom category colour

  if (t.completed) { const dn = document.createElement('span'); dn.className = 'tb-done'; dn.textContent = '✓'; el.appendChild(dn); }
  if (t.locked) { const lk = document.createElement('span'); lk.className = 'tb-lock'; lk.textContent = '🔒'; el.appendChild(lk); }
  if (imp === 'high') { const ex = document.createElement('span'); ex.className = 'tb-imp'; ex.textContent = '!'; el.appendChild(ex); }
  const prefix = t.client && t.team ? `${t.client} / ${t.team}` : (t.client || t.team || '');
  const name = document.createElement('span'); name.className = 'tb-name';
  name.style.cssText = 'flex:1;overflow:hidden;text-overflow:ellipsis;padding-right:4px';
  if (prefix) { const c = document.createElement('b'); c.className = 'tb-client'; c.textContent = prefix + ' — '; name.appendChild(c); }
  name.appendChild(document.createTextNode(t.name));
  el.appendChild(name);

  (t.tags || []).slice(0, 3).forEach(tg => {
    const p = document.createElement('span'); p.className = 'tb-tag'; p.textContent = tg; el.appendChild(p);
  });
  if (t.cost != null && t.cost !== '') {
    const c = document.createElement('span'); c.className = 'tb-cost'; c.textContent = gbp(t.cost); el.appendChild(c);
  }

  const st = t.subtasks || [], total = st.length, done = st.filter(s => s.done).length;
  if (total > 0) {
    const badge = document.createElement('div');
    badge.className = 'task-badge' + (done === total ? ' done' : '');
    badge.textContent = `${done}/${total}`;
    el.appendChild(badge);
  }
  // Resize handles (left / right edge) — omitted on locked tasks.
  if (!t.locked) ['l', 'r'].forEach(edge => {
    const h = document.createElement('div'); h.className = `tb-handle tb-${edge}`;
    h.addEventListener('mousedown', e => startResize(e, t.id, edge));
    el.appendChild(h);
  });

  const halfLbl = { pm: ' (PM start)', am: ' (AM end)', pmam: ' (PM→AM)' }[t.half] || '';
  el.title = `${prefix ? prefix + ' — ' : ''}${t.name}  |  ${t.start}${halfLbl} → ${t.end}`
    + `${total > 0 ? ' | ' + done + '/' + total + ' subtasks' : ''}`
    + `${imp !== 'normal' ? ' | ' + imp + ' priority' : ''}`
    + `${t.cost != null && t.cost !== '' ? ' | ' + gbp(t.cost) : ''}`
    + `${(t.tags || []).length ? ' | #' + t.tags.join(' #') : ''}`
    + `${t.created_by ? ' | added by ' + t.created_by : ''}`;
  el.dataset.id = t.id;
  el.addEventListener('click', e => { e.stopPropagation(); if (clickSuppressed()) return; openEdit(t.id); });
  el.addEventListener('mousedown', e => { e.stopPropagation(); startMove(e, t.id); });
  sec.appendChild(el);
}
